import { resolveVerticalStack, type Rect } from "@yjsgames/phaser-ui/panel-content";
import type { TextDirection } from "@yjsgames/i18n";
import { resolveOriginX } from "./directionalUi.js";

/** 합성 슬롯 한 칸 크기(px). */
export const MERGE_SLOT_SIZE = 56;
/** 슬롯 칸 사이 간격(px) — "+" 기호 자리. */
const SLOT_GAP = 22;
/** 콘텐츠 최상단 팁 캐러셀 영역 높이(px) — 제목 행 제거(하단 [합성]/[융합] 서브탭 라벨과 중복) 후 팁이 최상단. 합성/융합은 슬롯·결과·버튼·정렬행이 많아 26으로 축소(2줄 빡빡). */
const TIP_H = 26;
const BUTTON_H = 30;

export interface ModuleMergeLayout {
  /** 콘텐츠 최상단 팁 캐러셀 영역 (전체폭 컨테이너) — TipCarouselView 배치 기준(제목·프롬프트 행 제거 후 최상단). */
  readonly tip: Rect;
  /** 텍스트 origin x — LTR 0(좌)/RTL 1(우). 슬롯/버튼 미러 보조. */
  readonly textOriginX: number;
  /** 슬롯 3칸이 놓이는 행 영역(세로 위치 기준). */
  readonly slotsRow: Rect;
  /** 슬롯 3칸 개별 영역 (가로 중앙 배치, RTL이면 순서 미러). */
  readonly slotCells: readonly Rect[];
  /** 합성 결과 미리보기 칸. LTR은 우측 끝, RTL은 좌측 끝에 둔다. */
  readonly resultCell: Rect;
  readonly mergeButton: Rect;
  /** 정렬 필터 전용 행(버튼↔리스트 사이) — 텍스트 없는 전용 영역이라 필터버튼이 어떤 텍스트와도 안 겹친다(정적검증). */
  readonly sortRow: Rect;
  /** 하단 미배치 모듈 그리드(ScrollableList) 영역 — fill로 남은 세로 공간 전부. */
  readonly list: Rect;
}

/**
 * 합성 탭 세로 스택 레이아웃 (Phaser 비의존 순수). `resolveVerticalStack`로
 * title→prompt→슬롯행→버튼→리스트를 위에서 아래로 누적 배치해 **형제 영역 겹침을 구조적으로 차단**한다
 * (이전 인라인 좌표는 prompt와 슬롯행이 겹쳤다 — `moduleMergeLayout.test.ts`가 회귀 방지).
 * list는 fill이라 항상 남은 세로 공간 전부를 차지 → ScrollableList mask 클립으로 콘텐츠가 사라지지 않는다.
 * 슬롯 3칸과 결과 1칸은 슬롯행 rect 안에서 한 줄로 가로 중앙 배치(셀 사이 SLOT_GAP).
 * LTR이면 `재료+재료+재료=결과`, RTL이면 결과 칸을 좌측에 두고 슬롯0은 우측부터 배치한다.
 */
export function computeModuleMergeLayout(
  content: Rect,
  slotCount: number,
  dir: TextDirection = "ltr",
): ModuleMergeLayout {
  const s = resolveVerticalStack(
    {
      children: [
        { id: "tip", height: TIP_H, gapTop: 0 },
        { id: "slotsRow", height: MERGE_SLOT_SIZE, gapTop: 12 },
        { id: "mergeButton", height: BUTTON_H, gapTop: 12 },
        { id: "sortRow", height: 14, gapTop: 8 },
        { id: "list", fill: true, gapTop: 4 },
      ],
    },
    content,
  );
  const rtl = dir === "rtl";
  const boxCount = slotCount + 1; // material slots + result preview
  const totalW = boxCount * MERGE_SLOT_SIZE + slotCount * SLOT_GAP;
  const left = content.x + (content.width - totalW) / 2;
  const step = MERGE_SLOT_SIZE + SLOT_GAP;
  const visualCells: Rect[] = Array.from({ length: boxCount }, (_, i) => ({
    x: left + i * step,
    y: s.slotsRow!.y,
    width: MERGE_SLOT_SIZE,
    height: MERGE_SLOT_SIZE,
  }));
  const slotCells: Rect[] = [];
  for (let i = 0; i < slotCount; i++) {
    // RTL이면 슬롯0(첫 재료)을 우측 끝에 — resultCell은 좌측 끝.
    slotCells.push(visualCells[rtl ? slotCount - i : i]!);
  }
  return {
    tip: { x: content.x, y: s.tip!.y, width: content.width, height: s.tip!.height },
    textOriginX: resolveOriginX(dir),
    slotsRow: s.slotsRow!,
    slotCells,
    resultCell: visualCells[rtl ? 0 : slotCount]!,
    mergeButton: s.mergeButton!,
    sortRow: s.sortRow!,
    list: s.list!,
  };
}
